import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { map } from './Map';
import { geofenceToFeature, geometryToArea } from './mapUtil';
import { geofencesActions } from '../store';
import { useEffectAsync } from '../reactHelper';
import { useTranslation } from '../common/LocalizationProvider';
import { deleteItem, editItem } from '../fetch';
import { selectGeofencesArray } from '../createSelectorUtils';

const draw = new MapboxDraw({
  displayControlsDefault: false,
  controls: {
    polygon: true,
    line_string: true,
    trash: true
  },
  userProperties: true
});

MapGeofenceEdit.propTypes = {
  selectedGeofenceId: PropTypes.number,
  setIsOpen: PropTypes.func,
  setSelectedGeofenceId: PropTypes.func
};

export default function MapGeofenceEdit({ selectedGeofenceId, setIsOpen, setSelectedGeofenceId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslation();
  const geofences = useSelector(selectGeofencesArray);

  useEffect(() => {
    map.addControl(draw, 'top-left');
    return () => map.removeControl(draw);
  }, []);

  useEffectAsync(async () => {
    const listener = async (event) => {
      const feature = event.features[0];
      const newItem = { name: '', area: geometryToArea(feature.geometry) };
      draw.delete(feature.id);
      const response = await editItem('geofences', undefined, newItem);
      if (response.ok) {
        const item = await response.json();
        dispatch(geofencesActions.update([item]));
        setSelectedGeofenceId(item.id);
        setIsOpen(true);
        enqueueSnackbar(t('Geofences_Edit_Snackbar_Add'), {
          variant: 'success'
        });
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }
    };

    map.on('draw.create', listener);
    return () => map.off('draw.create', listener);
  }, [dispatch, navigate]);

  useEffectAsync(async () => {
    const listener = async (event) => {
      const feature = event.features[0];
      const response = await deleteItem('geofences', feature.id);
      if (response.ok) {
        dispatch(geofencesActions.delete(feature.id));
        enqueueSnackbar(t('Geofences_Edit_Snackbar_Delete'), {
          variant: 'success'
        });
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }
    };

    map.on('draw.delete', listener);
    return () => map.off('draw.delete', listener);
  }, [dispatch]);

  useEffectAsync(async () => {
    const listener = async (event) => {
      const feature = event.features[0];
      const item = Object.values(geofences).find((i) => i.id === feature.id);
      if (item) {
        const updatedItem = { ...item, area: geometryToArea(feature.geometry) };

        const response = await editItem('geofences', feature.id, updatedItem);
        if (response.ok) {
          dispatch(geofencesActions.update([await response.json()]));
          enqueueSnackbar(t('Geofences_Edit_Snackbar_Change'), {
            variant: 'success'
          });
        } else {
          const error = await response.text();
          const message = error.slice(0, error.indexOf('-'));
          enqueueSnackbar(message, { variant: 'error' });
        }
      }
    };

    map.on('draw.update', listener);
    return () => map.off('draw.update', listener);
  }, [dispatch, geofences]);

  useEffect(() => {
    draw.deleteAll();
    Object.values(geofences).forEach((geofence) => {
      draw.add(geofenceToFeature(geofence));
    });
  }, [geofences]);

  useEffect(() => {
    if (selectedGeofenceId) {
      const feature = draw.get(selectedGeofenceId);
      let { coordinates } = feature.geometry;
      if (Array.isArray(coordinates[0][0])) {
        [coordinates] = coordinates;
      }
      const bounds = coordinates.reduce(
        (bounds, coordinate) => bounds.extend(coordinate),
        new mapboxgl.LngLatBounds(coordinates[0], coordinates[1])
      );
      const canvas = map.getCanvas();
      map.fitBounds(bounds, { padding: Math.min(canvas.width, canvas.height) * 0.1 });
    }
  }, [selectedGeofenceId]);

  return null;
}
